import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Key, Package, Tag, ShoppingCart, Bot, Activity, Bell, Webhook, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DocsAPIReference() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const authExample = `// Authentication
// Include these headers with every API request:
const headers = {
  'Content-Type': 'application/json',
  'X-CI-Api-Key': 'your_api_key',       // Required
  'X-CI-Merchant-Id': 'merch_xyz789'    // Required
};

// API keys can be created in the dashboard under
// Settings → API Keys. Each key has scopes that
// control which endpoints it can access:
//
// Scopes: products:read, products:write, deals:read,
//         deals:write, orders:read, orders:write,
//         agents:read, agents:write, health:read,
//         events:write, webhooks:manage`;

  const endpoints = {
    products: [
      {
        method: 'GET',
        path: '/api/v1/products',
        credits: 1,
        description: 'List all products with pagination and filtering.',
        requestBody: null,
        response: `{
  "products": [
    {
      "id": "prod_abc123",
      "title": "Wireless Bluetooth Headphones",
      "price": 79.99,
      "compare_at_price": 99.99,
      "inventory": 42,
      "status": "active",
      "ci_score": 87,
      "created_at": "2026-01-10T08:00:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 20,
    "total": 156
  }
}`,
        jsExample: `const response = await fetch('https://api.commerceindex.ai/v1/products?page=1&per_page=20&status=active', {
  headers: {
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  }
});
const data = await response.json();
console.log(data.products);`,
      },
      {
        method: 'GET',
        path: '/api/v1/products/:id',
        credits: 1,
        description: 'Get a single product by ID with full details.',
        requestBody: null,
        response: `{
  "id": "prod_abc123",
  "title": "Wireless Bluetooth Headphones",
  "price": 79.99,
  "compare_at_price": 99.99,
  "description": "Premium wireless headphones...",
  "inventory": 42,
  "status": "active",
  "ci_score": 87,
  "images": ["https://cdn.example.com/img1.jpg"],
  "variants": [...],
  "created_at": "2026-01-10T08:00:00Z",
  "updated_at": "2026-03-15T12:30:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'POST',
        path: '/api/v1/products',
        credits: 2,
        description: 'Create a new product.',
        requestBody: `{
  "title": "Wireless Bluetooth Headphones",
  "price": 79.99,
  "compare_at_price": 99.99,
  "description": "Premium wireless headphones with ANC",
  "inventory": 100,
  "images": ["https://cdn.example.com/img1.jpg"],
  "tags": ["electronics", "audio"]
}`,
        response: `{
  "id": "prod_new456",
  "title": "Wireless Bluetooth Headphones",
  "price": 79.99,
  "status": "active",
  "created_at": "2026-03-17T10:00:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'PUT',
        path: '/api/v1/products/:id',
        credits: 2,
        description: 'Update an existing product.',
        requestBody: `{
  "price": 69.99,
  "inventory": 85
}`,
        response: `{
  "id": "prod_abc123",
  "title": "Wireless Bluetooth Headphones",
  "price": 69.99,
  "inventory": 85,
  "updated_at": "2026-03-17T10:05:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'DELETE',
        path: '/api/v1/products/:id',
        credits: 1,
        description: 'Delete a product (soft delete).',
        requestBody: null,
        response: `{ "deleted": true, "id": "prod_abc123" }`,
        jsExample: null,
      },
      {
        method: 'POST',
        path: '/api/v1/products/:id/match',
        credits: 5,
        description: 'Deep match a product against the Commerce Index catalog. Returns matching products across the marketplace.',
        requestBody: `{
  "match_depth": "deep",
  "include_pricing": true,
  "max_results": 10
}`,
        response: `{
  "matches": [
    {
      "product_id": "ci_prod_789",
      "title": "Similar Wireless Headphones",
      "similarity_score": 0.94,
      "price_range": { "min": 59.99, "max": 89.99 },
      "merchant_count": 12
    }
  ],
  "match_confidence": 0.91
}`,
        jsExample: `const response = await fetch('https://api.commerceindex.ai/v1/products/prod_abc123/match', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  },
  body: JSON.stringify({
    match_depth: 'deep',
    include_pricing: true,
    max_results: 10
  })
});
const matches = await response.json();`,
      },
      {
        method: 'GET',
        path: '/api/v1/products/:id/similar',
        credits: 3,
        description: 'Find similar products in the catalog based on attributes and CI Score.',
        requestBody: null,
        response: `{
  "similar": [
    {
      "product_id": "prod_def456",
      "title": "Premium Over-Ear Headphones",
      "price": 89.99,
      "ci_score": 82,
      "similarity": 0.87
    }
  ]
}`,
        jsExample: null,
      },
    ],
    deals: [
      {
        method: 'GET',
        path: '/api/v1/deals',
        credits: 1,
        description: 'List all deals with filtering by status, agent, and date range.',
        requestBody: null,
        response: `{
  "deals": [
    {
      "id": "deal_xyz789",
      "product_id": "prod_abc123",
      "discount_pct": 20,
      "original_price": 99.99,
      "deal_price": 79.99,
      "status": "active",
      "created_by": "deal_agent",
      "expires_at": "2026-03-20T23:59:59Z"
    }
  ],
  "pagination": { "page": 1, "per_page": 20, "total": 8 }
}`,
        jsExample: null,
      },
      {
        method: 'POST',
        path: '/api/v1/deals',
        credits: 3,
        description: 'Create a new deal on a product.',
        requestBody: `{
  "product_id": "prod_abc123",
  "discount_pct": 20,
  "starts_at": "2026-03-17T00:00:00Z",
  "expires_at": "2026-03-20T23:59:59Z",
  "max_redemptions": 100
}`,
        response: `{
  "id": "deal_new123",
  "product_id": "prod_abc123",
  "discount_pct": 20,
  "deal_price": 79.99,
  "status": "active",
  "created_at": "2026-03-17T10:00:00Z"
}`,
        jsExample: `const response = await fetch('https://api.commerceindex.ai/v1/deals', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  },
  body: JSON.stringify({
    product_id: 'prod_abc123',
    discount_pct: 20,
    starts_at: '2026-03-17T00:00:00Z',
    expires_at: '2026-03-20T23:59:59Z',
    max_redemptions: 100
  })
});`,
      },
      {
        method: 'PUT',
        path: '/api/v1/deals/:id',
        credits: 2,
        description: 'Update an existing deal.',
        requestBody: `{
  "discount_pct": 25,
  "expires_at": "2026-03-25T23:59:59Z"
}`,
        response: `{
  "id": "deal_xyz789",
  "discount_pct": 25,
  "deal_price": 74.99,
  "updated_at": "2026-03-17T10:15:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'DELETE',
        path: '/api/v1/deals/:id',
        credits: 1,
        description: 'Cancel/delete a deal.',
        requestBody: null,
        response: `{ "deleted": true, "id": "deal_xyz789" }`,
        jsExample: null,
      },
    ],
    orders: [
      {
        method: 'GET',
        path: '/api/v1/orders',
        credits: 1,
        description: 'List orders with filtering by status, date range, and deal association.',
        requestBody: null,
        response: `{
  "orders": [
    {
      "id": "order_123",
      "total": 79.99,
      "currency": "USD",
      "status": "paid",
      "items": [
        {
          "product_id": "prod_abc123",
          "quantity": 1,
          "unit_price": 79.99,
          "deal_id": "deal_xyz789"
        }
      ],
      "created_at": "2026-03-16T14:30:00Z"
    }
  ],
  "pagination": { "page": 1, "per_page": 20, "total": 342 }
}`,
        jsExample: null,
      },
      {
        method: 'GET',
        path: '/api/v1/orders/:id',
        credits: 1,
        description: 'Get a single order with full details.',
        requestBody: null,
        response: `{
  "id": "order_123",
  "total": 79.99,
  "currency": "USD",
  "status": "paid",
  "customer_ref": "cust_anon_456",
  "items": [...],
  "shipping": { "status": "shipped", "tracking": "1Z999AA10123456784" },
  "created_at": "2026-03-16T14:30:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'POST',
        path: '/api/v1/orders',
        credits: 2,
        description: 'Create/import an order (typically from your e-commerce platform).',
        requestBody: `{
  "external_id": "shopify_order_789",
  "total": 79.99,
  "currency": "USD",
  "items": [
    {
      "product_id": "prod_abc123",
      "quantity": 1,
      "unit_price": 79.99
    }
  ]
}`,
        response: `{
  "id": "order_new456",
  "external_id": "shopify_order_789",
  "status": "created",
  "created_at": "2026-03-17T10:00:00Z"
}`,
        jsExample: null,
      },
    ],
    agents: [
      {
        method: 'GET',
        path: '/api/v1/agents',
        credits: 1,
        description: 'List all configured agents with their status and trust tier.',
        requestBody: null,
        response: `{
  "agents": [
    {
      "type": "pricing_agent",
      "status": "active",
      "trust_tier": "supervised",
      "approval_mode": "auto",
      "decisions_count": 45,
      "success_rate": 0.89
    },
    {
      "type": "deal_agent",
      "status": "active",
      "trust_tier": "trusted",
      "approval_mode": "auto",
      "decisions_count": 234,
      "success_rate": 0.93
    }
  ]
}`,
        jsExample: `const response = await fetch('https://api.commerceindex.ai/v1/agents', {
  headers: {
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  }
});
const { agents } = await response.json();
agents.forEach(a => console.log(\`\${a.type}: \${a.trust_tier} (\${a.status})\`));`,
      },
      {
        method: 'GET',
        path: '/api/v1/agents/:type',
        credits: 1,
        description: 'Get detailed info for a specific agent type.',
        requestBody: null,
        response: `{
  "type": "pricing_agent",
  "status": "active",
  "trust_tier": "supervised",
  "approval_mode": "auto",
  "guardrails": {
    "confidence_threshold": 0.85,
    "max_actions_per_hour": 50,
    "max_financial_impact": 1000
  },
  "stats": {
    "total_decisions": 45,
    "success_rate": 0.89,
    "avg_confidence": 0.82,
    "days_active": 12
  }
}`,
        jsExample: null,
      },
      {
        method: 'POST',
        path: '/api/v1/agents/:type/enable',
        credits: 1,
        description: 'Enable an agent.',
        requestBody: null,
        response: `{ "type": "pricing_agent", "status": "active" }`,
        jsExample: null,
      },
      {
        method: 'POST',
        path: '/api/v1/agents/:type/disable',
        credits: 1,
        description: 'Disable an agent immediately. Cancels pending actions.',
        requestBody: null,
        response: `{ "type": "pricing_agent", "status": "disabled", "cancelled_actions": 2 }`,
        jsExample: null,
      },
      {
        method: 'PUT',
        path: '/api/v1/agents/:type/guardrails',
        credits: 2,
        description: 'Update guardrails for an agent.',
        requestBody: `{
  "approval_mode": "auto",
  "confidence_threshold": 0.85,
  "max_actions_per_hour": 30,
  "max_financial_impact": 500,
  "trust_tier_auto_upgrade": true
}`,
        response: `{
  "type": "pricing_agent",
  "guardrails": {
    "approval_mode": "auto",
    "confidence_threshold": 0.85,
    "max_actions_per_hour": 30,
    "max_financial_impact": 500,
    "trust_tier_auto_upgrade": true
  },
  "updated_at": "2026-03-17T10:00:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'GET',
        path: '/api/v1/agents/:type/decisions',
        credits: 1,
        description: 'List recent decisions made by an agent.',
        requestBody: null,
        response: `{
  "decisions": [
    {
      "id": "dec_abc123",
      "action": "price_adjustment",
      "product_id": "prod_abc123",
      "confidence": 0.92,
      "status": "approved",
      "financial_impact": 45.50,
      "created_at": "2026-03-17T09:30:00Z"
    }
  ],
  "pagination": { "page": 1, "per_page": 20, "total": 45 }
}`,
        jsExample: null,
      },
    ],
    health: [
      {
        method: 'GET',
        path: '/api/v1/health/score',
        credits: 1,
        description: 'Get your current CI Health Score with component breakdown.',
        requestBody: null,
        response: `{
  "score": 87,
  "grade": "A",
  "components": {
    "pricing_accuracy": 92,
    "inventory_health": 85,
    "fulfillment_rate": 88,
    "deal_performance": 83
  },
  "trend": "improving",
  "updated_at": "2026-03-17T08:00:00Z"
}`,
        jsExample: `const response = await fetch('https://api.commerceindex.ai/v1/health/score', {
  headers: {
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  }
});
const health = await response.json();
console.log(\`CI Score: \${health.score} (\${health.grade})\`);`,
      },
      {
        method: 'GET',
        path: '/api/v1/health/margin-guard',
        credits: 2,
        description: 'Get margin analysis and guard status for your products.',
        requestBody: null,
        response: `{
  "overall_margin": 0.34,
  "at_risk_products": 3,
  "alerts": [
    {
      "product_id": "prod_abc123",
      "current_margin": 0.08,
      "min_margin": 0.10,
      "recommendation": "Increase price or reduce deal discount"
    }
  ]
}`,
        jsExample: null,
      },
      {
        method: 'GET',
        path: '/api/v1/health/decision-graph',
        credits: 2,
        description: 'Get the decision graph showing agent activity and outcomes over time.',
        requestBody: null,
        response: `{
  "period": "7d",
  "total_decisions": 156,
  "approved": 142,
  "rejected": 8,
  "pending": 6,
  "daily": [
    { "date": "2026-03-11", "decisions": 22, "success_rate": 0.91 },
    { "date": "2026-03-12", "decisions": 25, "success_rate": 0.88 }
  ]
}`,
        jsExample: null,
      },
    ],
    events: [
      {
        method: 'POST',
        path: '/api/v1/events',
        credits: 1,
        description: 'Submit a custom event to the Commerce Index event pipeline.',
        requestBody: `{
  "type": "product.viewed",
  "data": {
    "product_id": "prod_abc123",
    "source": "homepage_carousel"
  }
}`,
        response: `{
  "event_id": "evt_new789",
  "type": "product.viewed",
  "received_at": "2026-03-17T10:00:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'GET',
        path: '/api/v1/events/types',
        credits: 1,
        description: 'List all available event types and their schemas.',
        requestBody: null,
        response: `{
  "event_types": [
    { "type": "product.viewed", "description": "Product page was viewed" },
    { "type": "product.purchased", "description": "Product was purchased" },
    { "type": "deal.redeemed", "description": "A deal was used in checkout" },
    { "type": "agent.decision", "description": "Agent made a decision" }
  ]
}`,
        jsExample: null,
      },
    ],
    webhooks: [
      {
        method: 'POST',
        path: '/api/v1/webhooks',
        credits: 1,
        description: 'Register a new webhook endpoint.',
        requestBody: `{
  "url": "https://your-server.com/webhooks/ci",
  "events": ["product.updated", "order.created", "deal.created"],
  "secret": "whsec_your_webhook_secret"
}`,
        response: `{
  "id": "wh_abc123",
  "url": "https://your-server.com/webhooks/ci",
  "events": ["product.updated", "order.created", "deal.created"],
  "status": "active",
  "created_at": "2026-03-17T10:00:00Z"
}`,
        jsExample: null,
      },
      {
        method: 'GET',
        path: '/api/v1/webhooks',
        credits: 1,
        description: 'List all registered webhooks.',
        requestBody: null,
        response: `{
  "webhooks": [
    {
      "id": "wh_abc123",
      "url": "https://your-server.com/webhooks/ci",
      "events": ["product.updated", "order.created"],
      "status": "active",
      "last_delivery": "2026-03-17T09:45:00Z"
    }
  ]
}`,
        jsExample: null,
      },
      {
        method: 'DELETE',
        path: '/api/v1/webhooks/:id',
        credits: 1,
        description: 'Delete a webhook endpoint.',
        requestBody: null,
        response: `{ "deleted": true, "id": "wh_abc123" }`,
        jsExample: null,
      },
    ],
  };

  const methodColors = {
    GET: { bg: 'bg-[#3B82F6]/20', text: 'text-[#3B82F6]', border: 'border-[#3B82F6]/30' },
    POST: { bg: 'bg-[#3EF1C7]/20', text: 'text-[#3EF1C7]', border: 'border-[#3EF1C7]/30' },
    PUT: { bg: 'bg-[#F59E0B]/20', text: 'text-[#F59E0B]', border: 'border-[#F59E0B]/30' },
    DELETE: { bg: 'bg-[#EF4444]/20', text: 'text-[#EF4444]', border: 'border-[#EF4444]/30' },
  };

  const sectionIcons = {
    products: <Package className="w-6 h-6 text-[#3EF1C7]" />,
    deals: <Tag className="w-6 h-6 text-[#3EF1C7]" />,
    orders: <ShoppingCart className="w-6 h-6 text-[#3EF1C7]" />,
    agents: <Bot className="w-6 h-6 text-[#3EF1C7]" />,
    health: <Activity className="w-6 h-6 text-[#3EF1C7]" />,
    events: <Bell className="w-6 h-6 text-[#3EF1C7]" />,
    webhooks: <Webhook className="w-6 h-6 text-[#3EF1C7]" />,
  };

  const sectionTitles = {
    products: 'Products',
    deals: 'Deals',
    orders: 'Orders',
    agents: 'Agents',
    health: 'Health',
    events: 'Events',
    webhooks: 'Webhooks',
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Reference
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          API Reference
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Complete API documentation for Commerce Index. All endpoints require authentication
          via API key and merchant ID headers.
        </p>
      </div>

      {/* Authentication */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-6 h-6 text-[#3EF1C7]" />
          Authentication
        </h2>
        <p className="text-[#94A3B8] mb-4">
          All API requests require an API key and merchant ID passed via headers.
          API keys can be scoped to limit access to specific resources.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(authExample, 'auth')}
          >
            {copiedCode === 'auth' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{authExample}</code>
          </pre>
        </div>
      </section>

      {/* Endpoint Sections */}
      {Object.entries(endpoints).map(([section, eps]) => (
        <section key={section} className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
            {sectionIcons[section]}
            {sectionTitles[section]}
          </h2>
          <div className="space-y-4">
            {eps.map((ep, i) => {
              const mc = methodColors[ep.method];
              const epId = `${section}-${i}`;
              return (
                <Card key={i} className="bg-[#0D1628] border-[#22304A]">
                  <CardHeader>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge className={`${mc.bg} ${mc.text} ${mc.border} font-mono text-xs`}>
                        {ep.method}
                      </Badge>
                      <code className="text-[#E2E8F0] text-sm font-mono">{ep.path}</code>
                      <Badge className="bg-[#22304A] text-[#94A3B8] border-[#22304A] text-xs ml-auto">
                        {ep.credits} credit{ep.credits > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <p className="text-[#94A3B8] text-sm mt-2">{ep.description}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {ep.requestBody && (
                      <div>
                        <p className="text-[#94A3B8] text-xs font-medium mb-2 uppercase tracking-wider">Request Body</p>
                        <div className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
                            onClick={() => copyToClipboard(ep.requestBody, `${epId}-req`)}
                          >
                            {copiedCode === `${epId}-req` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                          <pre className="bg-[#0B1120] text-[#E2E8F0] p-3 rounded-lg overflow-x-auto text-xs border border-[#22304A]">
                            <code>{ep.requestBody}</code>
                          </pre>
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-[#94A3B8] text-xs font-medium mb-2 uppercase tracking-wider">Response</p>
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
                          onClick={() => copyToClipboard(ep.response, `${epId}-res`)}
                        >
                          {copiedCode === `${epId}-res` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        </Button>
                        <pre className="bg-[#0B1120] text-[#E2E8F0] p-3 rounded-lg overflow-x-auto text-xs border border-[#22304A]">
                          <code>{ep.response}</code>
                        </pre>
                      </div>
                    </div>
                    {ep.jsExample && (
                      <div>
                        <p className="text-[#94A3B8] text-xs font-medium mb-2 uppercase tracking-wider">JavaScript Example</p>
                        <div className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
                            onClick={() => copyToClipboard(ep.jsExample, `${epId}-js`)}
                          >
                            {copiedCode === `${epId}-js` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                          <pre className="bg-[#0B1120] text-[#E2E8F0] p-3 rounded-lg overflow-x-auto text-xs border border-[#22304A]">
                            <code>{ep.jsExample}</code>
                          </pre>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ))}

      {/* What's Next */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">What's Next?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/docs/webhook-setup">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Webhook Setup <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure webhooks to receive real-time event notifications.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/guardrails">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Guardrails <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure safety limits for AI agent actions.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
